import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    SourceType,
    SourceTypeUpdated,
    SourceTypeOnServerCreated,
    selectSourceTypeById,
    selectLastCreatedSourceTypeId, selectSourceTypesActionLoading
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-source-type-edit',
    templateUrl: './source-type-edit.component.html',
    styleUrls: ['./source-type-edit.component.scss'],
})


export class SourceTypeEditComponent implements OnInit, OnDestroy {

    model: SourceType;
    oldModel: SourceType;

    sourceTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false;
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectSourceTypesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectSourceTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initSourceType();
                    }
                });
            } else {
                this.model = new SourceType();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initSourceType();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initSourceType() {
        this.createForm();
    }


    createForm() {
        this.sourceTypeForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Source Credibility '${
                this.model.name
                }'`;
        }
        return 'New Source Credibility';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.sourceTypeForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.sourceTypeForm.markAsPristine();
        this.sourceTypeForm.markAsUntouched();
        this.sourceTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.sourceTypeForm.controls;
        /** check form */
        if (this.sourceTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateSourceType(this.model);
        } else {
            this.createSourceType(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: SourceTypeModel
     */
    updateSourceType(_model: SourceType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateSourceType: Update<SourceType> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new SourceTypeUpdated({
            partialSourceType: updateSourceType,
            sourceType: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: SourceTypeModel
     */
    createSourceType(_model: SourceType) {
        this.store.dispatch(new SourceTypeOnServerCreated({sourceType: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedSourceTypeId),
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
